import {
  Button,
  CircularProgress,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useDebounce } from "../../../../utils/functions";
import React from "react";
import {
  useGetPreviousSearchQuery,
  useSearchMessagesQuery,
  useSyncMessagesQuery,
} from "../../../../services/chat";
import { ReactComponent as SearchChat } from "../../../../assets/images/search_chat.svg";
import { CiSearch } from "react-icons/ci";
import { useNavigate } from "react-router-dom";

type SearchMessagesProps = {
  setCurrentConversationId: (option: string) => void;
  wrapperRef: React.RefObject<HTMLDivElement>;
};

const SearchMessages: React.FC<SearchMessagesProps> = ({
  wrapperRef,
  setCurrentConversationId,
}) => {
  const metaData = useSelector((state: any) => state.metaData);
  const userId = metaData.userInfo["cognito:username"];
  const [searchedMessages, setSearchedMessages] = React.useState([]);
  const [query, setQuery] = React.useState("");
  const debouncedSearch = useDebounce(query, 500);
  const [searchType, setSearchType] = React.useState("recent");
  const [suggestions, setSuggestions] = React.useState<any>([]);
  const navigate = useNavigate();
  const theme = useTheme();
  const mode = theme.palette.mode;
  const [showSearchMessages, setShowSearchMessages] =
    React.useState<any>(undefined);
  const [showSearchSuggestions, setShowSearchSuggestions] =
    React.useState(false);
  const searchMessages = useSearchMessagesQuery(
    { userId, query },
    {
      skip: query ? query != debouncedSearch : true,
      refetchOnMountOrArgChange: true,
    }
  );
  const syncMessages = useSyncMessagesQuery(
    { userId },
    { skip: !debouncedSearch || debouncedSearch != query }
  );
  const previousSearch = useGetPreviousSearchQuery(
    { userId, searchType },
    { skip: !showSearchSuggestions }
  );
  React.useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSearchMessages]);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      wrapperRef.current &&
      wrapperRef.current.contains(event.target as Node)
    ) {
      if (showSearchMessages) {
        setShowSearchMessages(undefined);
        setQuery("");
        setSearchedMessages([]);
        setSuggestions([]);
      }
    }
  };

  React.useEffect(() => {
    if (!searchMessages.isFetching) {
      if (searchMessages.isSuccess) {
        if (
          searchMessages.data &&
          searchMessages.data.messages &&
          searchMessages.data.messages.length
        ) {
          setSearchedMessages(searchMessages.data.messages);
        } else {
          setSearchedMessages([]);
        }
      }
    }
  }, [searchMessages.isFetching]);

  const searchSuggestions = (msg: any) => {
    return (
      <div
        id="suggestions-msgs"
        className="searched-message poppins-regular"
        onClick={() => {
          if (!query) {
            setQuery(msg.messageContent);
          } else {
            setShowSearchMessages(true);
            setSearchedMessages([]);
            setCurrentConversationId(msg.conversationId);
            navigate(`/chat?conversation_id=${msg.conversationId}`);
          }
        }}
      >
        <div className="searched-message-metadata">
          <SearchChat
            width={24}
            height={24}
            style={{ marginTop: "3px" }}
            stroke={mode == "light" ? "white" : "#2C4A3A"}
            className={mode == "light" ? "chat-icon-light" : ""}
          />
          <Typography component="div" color="success" className="searched-name poppins-regular">
            {msg.messageContent}
          </Typography>
          <Typography component="div" color="success" className="searched-time poppins-regular">
            {new Date(msg.messageTimestamp)
              .toLocaleString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })
              .replace(" at ", ", ")}
          </Typography>
        </div>
        {msg.conversationId ? (
          <div
            className="searched-thread poppins-regular"
            style={{ color: mode == "dark" ? "grey" : "lightgrey" }}
          >
            Thread: {msg.thread_name}
          </div>
        ) : null}
      </div>
    );
  };

  React.useEffect(() => {
    if (!previousSearch.isFetching && previousSearch.isSuccess) {
      if (previousSearch.data) {
        let data: Array<{ messageContent: any; messageTimestamp: any }> = [];
        previousSearch.data[searchType + "Searches"].forEach(
          (search: { query: any; created_at: any }) => {
            data.push({
              messageContent: search.query,
              messageTimestamp: search.created_at,
            });
          }
        );
        setSuggestions(data);
      }
    }
  }, [previousSearch.isFetching]);

  React.useEffect(() => {
    if (debouncedSearch) {
      syncMessages.refetch();
    }
  }, [debouncedSearch]);

  return (
    <div className="search_messages">
      <div className="search_bar">
        <Tooltip title="Search for messages">
          <Button
            id="search"
            className="search_icon"
            onClick={() => setShowSearchMessages(!showSearchMessages)}
          >
            <CiSearch size={24} />
          </Button>
        </Tooltip>
        {showSearchMessages != undefined ? (
          <div className="search" id="search-msgs">
            <TextField
              variant="standard"
              placeholder="Search chat"
              value={query}
              // multiline
              onClick={() => {
                setShowSearchSuggestions(true);
                if (showSearchSuggestions && !suggestions.length) {
                  previousSearch.refetch();
                }
              }}
              onChange={(e: any) => {
                setQuery(e.target.value);
                if (!e.target.value) {
                  setSearchedMessages([]);
                }
              }}
              sx={{
                flex: 1,
                backgroundColor:
                  mode == "dark"
                    ? theme.palette.primary.contrastText
                    : theme.palette.secondary.contrastText,
                height: "100%",
                borderRadius: "10px",
                "& .MuiInputBase-input": {
                  color:
                    mode == "dark"
                      ? "black"
                      : theme.palette.primary.contrastText,
                  padding: "5px",
                  border: "1px solid #2a945f !important",
                  borderRadius: "10px",
                  height: "30px",
                  paddingLeft: "10px !important",
                },
                "& input::placeholder": {},
              }}
              type="text"
              className={`search-input
                ${showSearchMessages ? "search-open" : "search-close"}
                `}
            />
            {showSearchMessages ? (
              <div
                className="search-suggestions"
                style={{
                  backgroundColor:
                    mode == "dark"
                      ? theme.palette.primary.contrastText
                      : theme.palette.secondary.contrastText,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                    maxHeight: "50vh",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {searchMessages.isSuccess && searchedMessages.length ? (
                    searchedMessages.map((message: any) =>
                      searchSuggestions(message)
                    )
                  ) : !searchMessages.isUninitialized &&
                    !searchMessages.isFetching &&
                    !searchedMessages.length &&
                    debouncedSearch &&
                    query == debouncedSearch &&
                    !searchMessages.data.messages.length ? (
                    <div
                      style={{
                        color: mode == "dark" ? "grey" : "lightgrey",
                        fontSize: "14px",
                        width: "100%",
                        margin: "10px",
                        padding: "0px 10px",
                        cursor: "auto !important",
                      }}
                    >
                      No results found
                    </div>
                  ) : searchMessages.isFetching ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "10%",
                        margin: "10px 0px",
                      }}
                    >
                      <CircularProgress color="success" />
                    </div>
                  ) : !query ? (
                    <div style={{ width: "100%" }}>
                      {suggestions.map((suggestion: any) =>
                        searchSuggestions(suggestion)
                      )}
                      {(searchType == "recent" ||
                        (searchType == "all" && previousSearch.isFetching)) &&
                      suggestions.length == 3 ? (
                        <Typography component="div"
                          style={{
                            width: "100%",
                            textAlign: "center",
                            cursor: "pointer",
                            margin: "10px 0px",
                            fontSize: "13px",
                            fontWeight: "normal !important",
                          }}
                          color="success"
                          onClick={() => setSearchType("all")}
                        >
                          {previousSearch.isFetching ? (
                            <CircularProgress color="success" />
                          ) : (
                            "View more results"
                          )}
                        </Typography>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};
export default SearchMessages;
